/**
 * Unicode 密文字符 → 唯一展示字体。
 *
 * 密文表每个字符在对应 Unicode 区块内只使用一种字体（一个码点 → 一个 font-family）。
 * Excel 导出使用 cipher_excel_font_families.json（可含安装包专用族如 Unifont）；
 * Web / 管理后台 / 小程序使用本文件的 CIPHER_FONT_FAMILIES。
 */

import { lookupCipherFontKey } from './cipherFontBlocks';
import type { CipherFontKey } from './types';

export type { CipherFontKey } from './types';

/**
 * 各脚本区块对应的唯一字体族名（须与 cipherFontAssets 中加载的 family 一致）。
 * 统一使用 Noto 系列，保证 Web / 小程序各平台字形一致。
 */
export const CIPHER_FONT_FAMILIES: Record<CipherFontKey, string> = {
  cjk: 'Noto Sans SC',
  cjkCompatibility: 'Noto Sans SC',
  cjkStrokes: 'Noto Sans SC',
  symbolsSupplement: 'Noto Sans Symbols',
  latinSupplement: 'Noto Sans',
  cjkExtA: 'Noto Sans SC',
  korean: 'Noto Sans KR',
  japanese: 'Noto Sans JP',
  arabicPresentationA: 'Noto Sans Arabic',
  arabicPresentationB: 'Noto Sans Arabic',
  hebrewPresentation: 'Noto Sans Hebrew',
  arabic: 'Noto Sans Arabic',
  hebrew: 'Noto Sans Hebrew',
  greek: 'Noto Sans',
  armenian: 'Noto Sans Armenian',
  // Cyrillic U+0400-052F gets its own key (was incorrectly bundled with 'latin')
  cyrillic: 'Noto Sans',
  cyrillicExtC: 'Noto Sans',
  syriac: 'Noto Sans Syriac',
  thaana: 'Noto Sans Thaana',
  nko: 'Noto Sans NKo',
  devanagari: 'Noto Sans Devanagari',
  bengali: 'Noto Sans Bengali',
  gurmukhi: 'Noto Sans Gurmukhi',
  gujarati: 'Noto Sans Gujarati',
  oriya: 'Noto Sans Oriya',
  tamil: 'Noto Sans Tamil',
  telugu: 'Noto Sans Telugu',
  kannada: 'Noto Sans Kannada',
  malayalam: 'Noto Sans Malayalam',
  sinhala: 'Noto Sans Sinhala',
  thai: 'Noto Sans Thai',
  lao: 'Noto Sans Lao',
  tibetan: 'Noto Serif Tibetan',
  myanmar: 'Noto Sans Myanmar',
  georgian: 'Noto Sans Georgian',
  ethiopic: 'Noto Sans Ethiopic',
  cherokee: 'Noto Sans Cherokee',
  canadianAboriginal: 'Noto Sans Canadian Aboriginal',
  // Ogham (U+1680-169F) and Runic (U+16A0-16FF) are distinct scripts
  ogham: 'Noto Sans Ogham',
  oghamRunic: 'Noto Sans Runic',
  // Box Drawing + Block Elements live in Noto Sans SC (better coverage than Noto Sans Symbols)
  boxDrawing: 'Noto Sans SC',
  khmer: 'Noto Sans Khmer',
  mongolian: 'Noto Sans Mongolian',
  yi: 'Noto Sans Yi',
  lisu: 'Noto Sans Lisu',
  vai: 'Noto Sans Vai',
  bamum: 'Noto Sans Bamum',
  sylotiNagri: 'Noto Sans Syloti Nagri',
  phagspa: 'Noto Sans PhagsPa',
  saurashtra: 'Noto Sans Saurashtra',
  kayahLi: 'Noto Sans Kayah Li',
  rejang: 'Noto Sans Rejang',
  javanese: 'Noto Sans Javanese',
  cham: 'Noto Sans Cham',
  taiViet: 'Noto Sans Tai Viet',
  meeteiMayek: 'Noto Sans Meetei Mayek',
  limbu: 'Noto Sans Limbu',
  taiLe: 'Noto Sans Tai Le',
  newTaiLue: 'Noto Sans New Tai Lue',
  buginese: 'Noto Sans Buginese',
  balinese: 'Noto Sans Balinese',
  sundanese: 'Noto Sans Sundanese',
  batak: 'Noto Sans Batak',
  lepcha: 'Noto Sans Lepcha',
  olChiki: 'Noto Sans Ol Chiki',
  tifinagh: 'Noto Sans Tifinagh',
  glagolitic: 'Noto Sans Glagolitic',
  coptic: 'Noto Sans Coptic',
  math: 'Noto Sans Math',
  // 'symbols' covers arrows, box-drawing, geometric, dingbats, etc. — Noto Sans Symbols
  // (not Noto Sans Symbols 2 which is supplemental/historic and lacks these basic blocks)
  symbols: 'Noto Sans Symbols',
  fullwidth: 'Noto Sans SC',
  latin: 'Noto Sans',
  universal: 'Noto Sans',
};

/** @deprecated 使用 CIPHER_FONT_FAMILIES */
export const CIPHER_FONT_FAMILY_STACKS = CIPHER_FONT_FAMILIES;

/** 格式化为 CSS font-family 值（含空格的族名加引号） */
export function formatCipherFontFamily(family: string): string {
  return family.includes(' ') ? `"${family}"` : family;
}

/**
 * 小程序 loadFontFace 后若 woff2 缺字形，不会像浏览器那样自动回退系统字。
 * 追加 sans-serif，使缺字码点（如 U+21AA ↪）与后台/开发者工具一致。
 */
export function formatCipherFontFamilyForMp(family: string): string {
  const normalized = family.replace(/^["']|["']$/g, '');
  return `${formatCipherFontFamily(normalized)}, sans-serif`;
}

/** Web / Admin：与小程序一致追加 sans-serif，缺字时回退系统字体 */
export const formatCipherFontFamilyForWeb = formatCipherFontFamilyForMp;

/** 按 Unicode 区块返回字体键名 */
export function cipherFontKeyForCodepoint(cp: number): CipherFontKey {
  return lookupCipherFontKey(cp) ?? 'universal';
}

/** 返回该字符对应的唯一 font-family（无回退链） */
export function cipherFontFamilyForCodepoint(cp: number): string {
  const family = CIPHER_FONT_FAMILIES[cipherFontKeyForCodepoint(cp)];
  return formatCipherFontFamily(family);
}

export interface CipherFontRun {
  fontFamily: string;
  text: string;
}

/** 将密文按字体合并为连续片段（相邻同字体字符合并） */
export function buildCipherFontRuns(text: string): CipherFontRun[] {
  if (!text) return [];

  const runs: CipherFontRun[] = [];
  let currentFont: string | null = null;
  let buffer = '';

  for (const char of text) {
    const cp = char.codePointAt(0);
    if (cp === undefined) continue;
    const fontFamily = cipherFontFamilyForCodepoint(cp);
    if (fontFamily === currentFont) {
      buffer += char;
      continue;
    }
    if (buffer && currentFont) {
      runs.push({ fontFamily: currentFont, text: buffer });
    }
    currentFont = fontFamily;
    buffer = char;
  }

  if (buffer && currentFont) {
    runs.push({ fontFamily: currentFont, text: buffer });
  }

  return runs;
}

// ─── BiDi normalization ───────────────────────────────────────────────────────

/**
 * 去除密文中的 Unicode 双向控制字符（与小程序 stripBidiMarks 和
 * Python format_cipher_display_text 行为一致）。
 * 在渲染前调用以避免 Arabic/Hebrew 密文在浏览器中触发非预期的文字重排。
 */
const BIDI_MARKS_RE = /[\u2066\u2067\u2068\u2069\u200E\u200F\u202A-\u202E]/g;

export function stripCipherBidiMarks(text: string): string {
  return text ? text.replace(BIDI_MARKS_RE, '') : text;
}
